"use client";

import { useCallback, useRef, useState } from "react";
import {
  Checkbox,
  ErrorNotice,
  Field,
  Notice,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  HINT,
  LABEL,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import {
  CvdCanvas,
  exportSheet,
  type SourceImage,
} from "@/components/tools/color-charts/cvd-canvas";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { CHART_FONT } from "@/lib/tool-kit/charts/text";
import {
  type CvdType,
  fitWithin,
  sampleImagePixels,
} from "@/lib/tool-kit/color-charts/cvd";
import { downloadBlob } from "@/lib/tool-kit/download";

/** English strings. A translated content file overrides these through `ui`. */
export const COLOR_BLINDNESS_SIMULATOR_STRINGS = {
  fileLabel: "Image to preview — PNG, JPEG, GIF, WebP or SVG",
  fileHint:
    "The picture is opened by your browser and drawn on a canvas on this page. It is never uploaded.",
  useSample: "Use the sample image",
  sampleName: "Sample image",
  loading: "Reading the image…",
  unreadable:
    "This file could not be read as an image. Try a PNG, JPEG, GIF, WebP or SVG file.",
  tooLarge: "That file is over {size} MB. Export a smaller copy and try again.",
  emptyPrompt:
    "Choose an image, or use the sample image, to see how it looks to people with color vision deficiency.",
  scaleNote:
    "Large pictures are scaled down to {size} pixels on the long side before they are processed.",
  viewsHeading: "Which views to show",
  severityLabel: "Severity of red, green and blue deficiency: {percent}%",
  severityHint:
    "100% is dichromacy — that cone type is missing (protanopia, deuteranopia, tritanopia). Lower values are anomalous trichromacy, where the cone type responds weakly (protanomaly, deuteranomaly, tritanomaly). Achromatopsia has no severity.",
  results: "Side-by-side views",
  original: "Original",
  protan100: "Protanopia — no red cones",
  protanPartial: "Protanomaly — weak red cones ({percent}%)",
  deutan100: "Deuteranopia — no green cones",
  deutanPartial: "Deuteranomaly — weak green cones ({percent}%)",
  tritan100: "Tritanopia — no blue cones",
  tritanPartial: "Tritanomaly — weak blue cones ({percent}%)",
  achroma: "Achromatopsia — no color (luminance only)",
  viewAria: "{view}: {image}",
  downloadPng: "Download PNG",
  downloadSheet: "Download all as one image",
  exportFailed:
    "This browser could not create the PNG. Try another browser or save each view separately.",
  needAView: "Turn on at least one view to compare it with the original.",
  caveat:
    "These are simulations from a published model, not a diagnosis. Screens, color management and individual vision all differ, so use them to find problems — then fix the design so it does not rely on color alone.",
} as const;

type Strings = typeof COLOR_BLINDNESS_SIMULATOR_STRINGS;
type Shown = Record<CvdType, boolean>;

const MAX_SIDE = 900;
const MAX_MB = 25;
const SAMPLE_SIZE = { width: 480, height: 270 };
const TYPES: readonly CvdType[] = ["protan", "deutan", "tritan", "achroma"];

const FULL_LABEL = {
  protan: "protan100",
  deutan: "deutan100",
  tritan: "tritan100",
} as const;
const PARTIAL_LABEL = {
  protan: "protanPartial",
  deutan: "deutanPartial",
  tritan: "tritanPartial",
} as const;

function viewLabel(
  type: CvdType,
  severity: number,
  strings: Strings,
  percent: string
): string {
  if (type === "achroma") {
    return strings.achroma;
  }
  const key = severity >= 100 ? FULL_LABEL[type] : PARTIAL_LABEL[type];
  return t(strings[key], { percent });
}

function readImage(file: File): Promise<SourceImage> {
  const url = URL.createObjectURL(file);
  return new Promise<SourceImage>((resolve, reject) => {
    const image = new Image();
    image.decoding = "async";
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("unreadable"));
    };
    image.onload = () => {
      URL.revokeObjectURL(url);
      const size = fitWithin(
        image.naturalWidth || 800,
        image.naturalHeight || 600,
        MAX_SIDE
      );
      const canvas = document.createElement("canvas");
      canvas.width = size.width;
      canvas.height = size.height;
      const context = canvas.getContext("2d", { willReadFrequently: true });
      if (!context) {
        reject(new Error("unreadable"));
        return;
      }
      context.drawImage(image, 0, 0, size.width, size.height);
      const pixels = context.getImageData(0, 0, size.width, size.height);
      resolve({ ...size, data: pixels.data, name: file.name });
    };
    image.src = url;
  });
}

export default function ColorBlindnessSimulatorWidget() {
  const strings = useToolUi(COLOR_BLINDNESS_SIMULATOR_STRINGS);
  const fmt = useToolNumber();
  const [source, setSource] = useState<SourceImage | null>(null);
  const [status, setStatus] = useState<"idle" | "loading" | "error" | "large">(
    "idle"
  );
  const [exportFailed, setExportFailed] = useState(false);
  const [severity, setSeverity] = useState(100);
  const [shown, setShown] = useState<Shown>({
    protan: true,
    deutan: true,
    tritan: true,
    achroma: true,
  });
  const canvases = useRef(new Map<string, HTMLCanvasElement>());
  const register = useCallback(
    (id: string, canvas: HTMLCanvasElement | null) => {
      if (canvas) {
        canvases.current.set(id, canvas);
      } else {
        canvases.current.delete(id);
      }
    },
    []
  );

  async function onFile(file: File | undefined) {
    if (!file) {
      return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      setStatus("large");
      return;
    }
    setStatus("loading");
    try {
      setSource(await readImage(file));
      setStatus("idle");
    } catch {
      setStatus("error");
    }
  }

  function loadSample() {
    setSource({
      ...SAMPLE_SIZE,
      data: sampleImagePixels(SAMPLE_SIZE.width, SAMPLE_SIZE.height),
      name: strings.sampleName,
    });
    setStatus("idle");
  }

  const percent = fmt(severity);
  const visible = TYPES.filter((type) => shown[type]);

  async function saveSheet() {
    if (!source) {
      return;
    }
    const panels = ["original", ...visible]
      .map((id) => ({
        id,
        label:
          id === "original"
            ? strings.original
            : viewLabel(id as CvdType, severity, strings, percent),
        canvas: canvases.current.get(id),
      }))
      .filter(
        (
          panel
        ): panel is { id: string; label: string; canvas: HTMLCanvasElement } =>
          panel.canvas !== undefined
      );
    const blob = await exportSheet(panels, CHART_FONT);
    if (blob) {
      setExportFailed(false);
      downloadBlob("color-blindness-simulation.png", blob);
    } else {
      setExportFailed(true);
    }
  }

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <Field hint={strings.fileHint} id="cbs-file" label={strings.fileLabel}>
          <input
            accept="image/*"
            className="block w-full text-foreground text-sm file:mr-4 file:rounded-lg file:border file:border-border file:bg-card file:px-4 file:py-2 file:font-medium file:text-foreground file:text-sm"
            id="cbs-file"
            onChange={(event) => onFile(event.target.files?.[0])}
            type="file"
          />
        </Field>
        <button className={BUTTON_SECONDARY} onClick={loadSample} type="button">
          {strings.useSample}
        </button>
      </div>
      <div aria-live="polite" className="space-y-2">
        {status === "loading" ? (
          <p className={HINT}>{strings.loading}</p>
        ) : null}
        {status === "error" ? (
          <ErrorNotice>{strings.unreadable}</ErrorNotice>
        ) : null}
        {status === "large" ? (
          <ErrorNotice>
            {t(strings.tooLarge, { size: fmt(MAX_MB) })}
          </ErrorNotice>
        ) : null}
        {source ? null : <Notice>{strings.emptyPrompt}</Notice>}
      </div>
      <fieldset className="space-y-3">
        <legend className={LABEL}>{strings.viewsHeading}</legend>
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          {TYPES.map((type) => (
            <Checkbox
              checked={shown[type]}
              key={type}
              label={viewLabel(type, 100, strings, "100")}
              onChange={(checked) => setShown({ ...shown, [type]: checked })}
            />
          ))}
        </div>
      </fieldset>
      <div className="space-y-2">
        <label className={LABEL} htmlFor="cbs-severity">
          {t(strings.severityLabel, { percent })}
        </label>
        <input
          className="w-full accent-primary"
          id="cbs-severity"
          max={100}
          min={0}
          onChange={(event) => setSeverity(Number(event.target.value))}
          step={10}
          type="range"
          value={severity}
        />
        <p className={HINT}>{strings.severityHint}</p>
      </div>
      {source ? (
        <section className="space-y-4">
          <h2 className={WIDGET_H2}>{strings.results}</h2>
          {visible.length === 0 ? <Notice>{strings.needAView}</Notice> : null}
          <div className="grid gap-4 sm:grid-cols-2">
            {["original", ...visible].map((id) => {
              const type = id === "original" ? null : (id as CvdType);
              const label = type
                ? viewLabel(type, severity, strings, percent)
                : strings.original;
              return (
                <CvdCanvas
                  ariaLabel={t(strings.viewAria, {
                    view: label,
                    image: source.name,
                  })}
                  downloadLabel={strings.downloadPng}
                  filename={`color-blindness-${id}.png`}
                  id={id}
                  key={id}
                  label={label}
                  register={register}
                  severity={severity / 100}
                  source={source}
                  type={type}
                />
              );
            })}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button
              className={BUTTON_PRIMARY}
              onClick={saveSheet}
              type="button"
            >
              {strings.downloadSheet}
            </button>
          </div>
          {exportFailed ? (
            <ErrorNotice>{strings.exportFailed}</ErrorNotice>
          ) : null}
          <p className={HINT}>
            {t(strings.scaleNote, { size: fmt(MAX_SIDE) })}
          </p>
          <p className={HINT}>{strings.caveat}</p>
        </section>
      ) : null}
    </div>
  );
}
